import { useEffect, useMemo, useState } from "react";
import { Navigate, Link } from "react-router-dom";
import { RefreshCw, Search, Trash2, ExternalLink, PhoneCall } from "lucide-react";
import { toast } from "sonner";

import { api, useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { fmtDate, relTime } from "@/pages/admin/dates";
import { scoreFor } from "@/pages/admin/leadScore";

const TIER_META = {
  pro: { label: "Pro", tone: "bg-[#EDEAE4] text-[#2A2724]" },
  foundation: { label: "Foundation", tone: "bg-gold/20 text-[#8B6F1F]" },
  platinum: { label: "Platinum", tone: "bg-[#2A2724] text-white" },
};

export default function AdminPricingLeads() {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [q, setQ] = useState("");
  const [busyId, setBusyId] = useState(null);

  const load = async () => {
    setLoading(true);
    setError("");
    try {
      const { data } = await api.get("/admin/pricing-leads?limit=500");
      setRows(Array.isArray(data) ? data : []);
    } catch (e) {
      setError("Failed to load pricing leads");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!user || user.role !== "admin") return;
    load();
  }, [user]);

  const remove = async (id, email) => {
    if (!window.confirm(`Delete pricing lead for ${email}? This can't be undone.`)) return;
    setBusyId(id);
    try {
      await api.delete(`/admin/pricing-leads/${id}`);
      setRows((rs) => rs.filter((r) => r.id !== id));
      toast.success("Lead removed.");
    } catch {
      toast.error("Could not delete lead.");
    } finally {
      setBusyId(null);
    }
  };

  const markContacted = async (id) => {
    setBusyId(id);
    try {
      const { data } = await api.post(`/admin/pricing-leads/${id}/contacted`);
      setRows((rs) => rs.map((r) => (r.id === id ? { ...r, ...data } : r)));
      toast.success("Marked contacted.");
    } catch {
      toast.error("Could not update lead.");
    } finally {
      setBusyId(null);
    }
  };

  const filtered = useMemo(() => {
    if (!q.trim()) return rows;
    const needle = q.trim().toLowerCase();
    return rows.filter((r) =>
      [r.name, r.email, r.phone, r.tier].filter(Boolean).join(" ").toLowerCase().includes(needle)
    );
  }, [rows, q]);

  if (user === null) return null;
  if (!user) return <Navigate to="/login" replace state={{ from: "/admin/pricing-leads" }} />;
  if (user.role !== "admin") return <Navigate to="/" replace />;

  return (
    <div className="min-h-screen bg-[#F7F5F2] px-6 py-10 text-[#2A2724]">
      <div className="mx-auto max-w-6xl">
        <div className="mb-8 flex flex-wrap items-baseline justify-between gap-3">
          <div>
            <p className="text-xs uppercase tracking-[0.28em] text-[#8B6F1F]">Admin</p>
            <h1 className="mt-1 font-serif text-4xl font-light">Pricing reveal leads</h1>
            <p className="mt-1 text-sm text-[#6B6B6B]">
              {rows.length} lead{rows.length === 1 ? "" : "s"} · newest first · auto-synced to the
              main waitlist as{" "}
              <Link
                to="/admin/waitlist?cohort=pricing_lead"
                className="underline decoration-gold/60 underline-offset-4 hover:text-[#8B6F1F]"
              >
                pricing_lead
              </Link>{" "}
              cohort
            </p>
          </div>
          <div className="flex items-center gap-2">
            <div className="relative">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[#9a9a9a]" />
              <Input
                data-testid="pricing-leads-search"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Filter by name / email / phone / tier"
                className="w-72 rounded-full pl-9"
              />
            </div>
            <Button
              data-testid="pricing-leads-refresh"
              onClick={load}
              variant="outline"
              className="rounded-full border-black/10"
            >
              <RefreshCw className="mr-2 h-4 w-4" />
              Refresh
            </Button>
          </div>
        </div>

        {loading && (
          <p className="rounded-2xl border border-black/10 bg-white p-6 text-sm text-[#6B6B6B]">
            Loading…
          </p>
        )}
        {error && (
          <p
            data-testid="pricing-leads-error"
            className="rounded-2xl border border-[#a13a3a]/30 bg-[#F8ECEC] p-6 text-sm text-[#a13a3a]"
          >
            {error}
          </p>
        )}
        {!loading && !error && filtered.length === 0 && (
          <p
            data-testid="pricing-leads-empty"
            className="rounded-2xl border border-black/10 bg-white p-6 text-sm text-[#6B6B6B]"
          >
            No pricing leads yet. They'll appear the moment a visitor unlocks the price list on{" "}
            <Link to="/pricing" className="underline underline-offset-4 hover:text-[#8B6F1F]">
              /pricing
            </Link>
            .
          </p>
        )}
        {!loading && !error && filtered.length > 0 && (
          <div
            data-testid="pricing-leads-table"
            className="overflow-hidden rounded-2xl border border-black/10 bg-white"
          >
            <table className="w-full text-sm">
              <thead className="bg-[#EDEAE4] text-left text-xs uppercase tracking-[0.14em] text-[#6B6B6B]">
                <tr>
                  <th className="px-4 py-3">Score</th>
                  <th className="px-4 py-3">When</th>
                  <th className="px-4 py-3">Name</th>
                  <th className="px-4 py-3">Email</th>
                  <th className="px-4 py-3">Phone</th>
                  <th className="px-4 py-3">Tier</th>
                  <th className="px-4 py-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((r) => {
                  const meta = TIER_META[r.tier] || {
                    label: r.tier || "—",
                    tone: "bg-[#F7F5F2] text-[#6B6B6B]",
                  };
                  const score = scoreFor(r);
                  return (
                    <tr
                      key={r.id}
                      data-testid={`pricing-lead-row-${r.id}`}
                      data-score={score.key}
                      className="border-t border-black/5 hover:bg-[#F7F5F2]"
                    >
                      <td className="whitespace-nowrap px-4 py-3">
                        <span
                          data-testid={`pricing-lead-score-${r.id}`}
                          className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-[0.65rem] font-semibold uppercase tracking-[0.14em] ${score.tone}`}
                        >
                          {score.label}
                        </span>
                        {r.nudged_at && !r.contacted_at && (
                          <span
                            title={`Auto-nudge sent ${relTime(r.nudged_at)}`}
                            className="ml-2 inline-block h-1.5 w-1.5 rounded-full bg-gold align-middle"
                          />
                        )}
                      </td>
                      <td className="whitespace-nowrap px-4 py-3">
                        <div className="text-[#2A2724]">{fmtDate(r.created_at)}</div>
                        <div className="text-[0.68rem] uppercase tracking-[0.14em] text-[#9a9a9a]">
                          {relTime(r.created_at)}
                        </div>
                      </td>
                      <td className="whitespace-nowrap px-4 py-3 font-medium text-[#2A2724]">
                        {r.name}
                      </td>
                      <td className="whitespace-nowrap px-4 py-3">
                        <a
                          href={`mailto:${r.email}`}
                          className="text-[#2A2724] underline decoration-black/20 underline-offset-4 hover:decoration-gold"
                        >
                          {r.email}
                        </a>
                      </td>
                      <td className="whitespace-nowrap px-4 py-3">
                        <a
                          href={`tel:${r.phone.replace(/[^+0-9]/g, "")}`}
                          className="text-[#2A2724] underline decoration-black/20 underline-offset-4 hover:decoration-gold"
                        >
                          {r.phone}
                        </a>
                      </td>
                      <td className="whitespace-nowrap px-4 py-3">
                        <span
                          className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-[0.65rem] font-medium uppercase tracking-[0.14em] ${meta.tone}`}
                        >
                          {meta.label}
                        </span>
                      </td>
                      <td className="whitespace-nowrap px-4 py-3 text-right">
                        <div className="inline-flex items-center gap-1">
                          {!r.contacted_at && (
                            <button
                              onClick={() => markContacted(r.id)}
                              disabled={busyId === r.id}
                              data-testid={`pricing-lead-mark-contacted-${r.id}`}
                              title="Mark contacted"
                              className="inline-flex h-8 w-8 items-center justify-center rounded-full text-[#2f7d3f] hover:bg-[#E9F5EC] disabled:opacity-50"
                            >
                              <PhoneCall className="h-4 w-4" />
                            </button>
                          )}
                          <Link
                            to={`/admin/waitlist?q=${encodeURIComponent(r.email)}`}
                            title="Open in main CRM"
                            data-testid={`pricing-lead-open-${r.id}`}
                            className="inline-flex h-8 w-8 items-center justify-center rounded-full text-[#6B6B6B] hover:bg-[#EDEAE4] hover:text-[#2A2724]"
                          >
                            <ExternalLink className="h-4 w-4" />
                          </Link>
                          <button
                            onClick={() => remove(r.id, r.email)}
                            disabled={busyId === r.id}
                            data-testid={`pricing-lead-delete-${r.id}`}
                            title="Delete lead"
                            className="inline-flex h-8 w-8 items-center justify-center rounded-full text-[#a13a3a] hover:bg-[#F8ECEC] disabled:opacity-50"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
